/**
 * soundAlerts.js
 * Sistema de alertas sonoras nativas (Web Audio API) para Transportes Emilia / EmiGo.
 * Diseñado para avisar a las locutoras cuando un chat de WhatsApp requiere atención humana.
 * Sin dependencias externas de archivos .mp3 (cero 404s, cero CORS, latencia 0ms).
 * No requiere botones de activar/desactivar.
 */

let sharedAudioContext = null;
let lastAlertTimestamp = 0;
const ALERT_COOLDOWN_MS = 3500; // Evita ráfagas o sonidos solapados

// Inicializa o retorna el AudioContext compartido
export const getAudioContext = () => {
  if (typeof window === 'undefined') return null;

  if (!sharedAudioContext) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      sharedAudioContext = new AudioCtx();
    }
  }

  // Si está suspendido por la política de autoplay del navegador, reanudar
  if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().catch(() => {});
  }

  return sharedAudioContext;
};

// Autodesbloqueo con la primera interacción del usuario en la ventana
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    try {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume();
      }
    } catch (e) {}
  };

  ['click', 'keydown', 'pointerdown', 'touchstart'].forEach(eventName => {
    window.addEventListener(eventName, unlockAudio, { once: true, passive: true });
  });
}

/**
 * Reproduce un sonido armónico y elegante de dos notas ("Ding-Dong" concierge/chime)
 * para alertar a la locutora que un pasajero requiere atención humana.
 */
export const playLocutoraAlertSound = () => {
  const nowMs = Date.now();
  if (nowMs - lastAlertTimestamp < ALERT_COOLDOWN_MS) {
    // En cooldown para no aturdir ni duplicar
    return;
  }
  lastAlertTimestamp = nowMs;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const t = ctx.currentTime;

    // Helper para emitir un tono de campana con envolvente suave (sin chasquidos)
    const playBellTone = (frequency, startTime, duration, gainLevel = 0.25) => {
      // Oscilador principal sinusoidal cálido
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency, startTime);

      // Oscilador armónico sutil para brillo acústico
      const overtone = ctx.createOscillator();
      const overtoneGain = ctx.createGain();
      overtone.type = 'triangle';
      overtone.frequency.setValueAtTime(frequency * 2, startTime);

      // Envolventes de ganancia (fade-in rápido para evitar clicks, decaimiento exponencial)
      gain.gain.setValueAtTime(0.001, startTime);
      gain.gain.linearRampToValueAtTime(gainLevel, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      overtoneGain.gain.setValueAtTime(0.001, startTime);
      overtoneGain.gain.linearRampToValueAtTime(gainLevel * 0.15, startTime + 0.02);
      overtoneGain.gain.exponentialRampToValueAtTime(0.001, startTime + (duration * 0.6));

      osc.connect(gain);
      overtone.connect(overtoneGain);
      gain.connect(ctx.destination);
      overtoneGain.connect(ctx.destination);

      osc.start(startTime);
      overtone.start(startTime);
      osc.stop(startTime + duration + 0.05);
      overtone.stop(startTime + duration + 0.05);
    };

    // Nota 1: Re 5 (D5 ~ 587.33 Hz)
    playBellTone(587.33, t + 0.05, 0.38, 0.28);

    // Nota 2: La 5 (A5 ~ 880.00 Hz) - Interválica ascendente agradable y clara
    playBellTone(880.00, t + 0.32, 0.55, 0.32);

  } catch (err) {
    console.warn("No se pudo reproducir la alerta sonora de locutora:", err);
  }
};
